import { useEffect, useRef, useState } from 'react';

import { convertLegacyRemInCss } from 'helpers/legacyRem';

import { useAppInfo } from 'hooks/useAppInfo/useAppInfo';

import { isNil } from 'lodash-es';
import { compileStringAsync } from 'sass';

export interface ClientStyleProps {
  disableRemoteStyles?: boolean;
  id?: string;
  children?: React.ReactNode;
  /** Convert server `custom_css` rem values from the legacy root to 16px-root rem. */
  convertLegacyRem?: boolean;
}
/**
 * wrapInQuotes is to support multi-word fonts such as "open sans". Guards against preexisting quotation just in case.
 */
function wrapInQuotes(cssValue: string | undefined) {
  return cssValue ? `"${cssValue.replace(/^["']|["']$/g, '')}"` : cssValue;
}

const cssVarKeys = [
  '--company-color',
  '--primary-color',
  '--secondary-color',
  '--heading-family',
  '--body-family',
  '--base-family',
] as const;

export function ClientStyle({ id, convertLegacyRem = true, disableRemoteStyles = false, children }: ClientStyleProps) {
  const { data, isSuccess } = useAppInfo(id);

  const styleRef = useRef(document.createElement('style'));
  const [stylesLoaded, setStylesLoaded] = useState(false);

  useEffect(() => {
    const style = styleRef.current;

    const addStyle = async () => {
      if (isNil(data)) {
        if (isSuccess) console.error('Failed to add style tag for app', id);
        return;
      }

      try {
        if (data?.custom_css) {
          const { css } = await compileStringAsync(
            convertLegacyRemInCss(data.custom_css, { enabled: convertLegacyRem }),
          );

          style.textContent = css;
          style.id = 'client-style-custom';

          document.head.appendChild(style);
        }

        /**
         * These keys are named so for legacy reasons.
         * TODO: Refactor this to use names that make sense.
         */
        const variables = {
          '--company-color': data?.primary_color,
          '--primary-color': data?.primary_color,
          '--secondary-color': data?.secondary_color,
          '--heading-family': wrapInQuotes(data?.font),
          '--body-family': wrapInQuotes(data?.body_font),
          '--base-family': wrapInQuotes(data?.body_font),
        } as const;

        Object.entries(variables).forEach(([key, value]) => {
          if (value == null) {
            console.error('Failed to set variable', key, 'because it is undefined');
            return;
          }

          document.documentElement.style.setProperty(key, value);
        });
      } catch (error) {
        console.error(error);
      } finally {
        setStylesLoaded(true);
      }
    };

    void addStyle();

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }

      cssVarKeys.forEach((key) => {
        document.documentElement.style.removeProperty(key);
      });
    };
  }, [data, disableRemoteStyles, isSuccess, id]);

  const noCustomCss = isSuccess && !data;

  if (stylesLoaded || noCustomCss) {
    return children;
  }

  return null;
}
